import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCatererById, uploadCatererLogo } from '../API/caterers';
import { showErrorToast, showSuccessToast } from '../components/common';
import AddEditCatererModal from '../modal/AddEditCatererModal';

const CatererProfile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [caterer, setCaterer] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCaterer, setEditingCaterer] = useState(null);
  
  // Ref to prevent duplicate API calls
  const isFetchingRef = useRef(false);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingCaterer(null);
  };

  const handleModalSuccess = (updatedCaterer) => {
    setCaterer(updatedCaterer);
    setIsModalOpen(false);
    setEditingCaterer(null);
  };

  useEffect(() => {
      const currentUser = JSON.parse(localStorage.getItem('CurrentUser'));
    if (currentUser?.id) {
      fetchCatererData();
    }
  
    return () => {
      setCaterer(null);
      // setLoading(false);
      setError(null);
      // isFetchingRef.current = false;
    };
  }, []);

  const fetchCatererData = async () => {
    // Prevent duplicate calls
    if (isFetchingRef.current) {
      return;
    }
  
    isFetchingRef.current = true;
    
    try {
      setLoading(true);
      setError(null);
      const currentUser = JSON.parse(localStorage.getItem('CurrentUser'));
      const response = await getCatererById(currentUser?.id);
      
      if (response.status === 200 && response.data.success) {
        setCaterer(response.data.data.caterer);
        setSubscription(response.data.data.subscription || null);
        // Set logo preview if logo exists
        if (response.data.data.caterer.logoUrl) {
          setLogoPreview(response.data.data.caterer.logoUrl);
        }
      } else {
        setError('Failed to fetch caterer data');
        showErrorToast('Failed to fetch caterer data');
      }
    } catch (error) {
      console.error('Error fetching caterer:', error);
      setError('Error fetching caterer data');
      showErrorToast('Error fetching caterer data');
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setLogoFile(file);
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;

    try {
      setUploadingLogo(true);
      const response = await uploadCatererLogo(caterer._id, logoFile);
      
      if (response.status === 200 && response.data.success) {
        // Update the caterer state with the new logo URL
        setCaterer(prev => ({
          ...prev,
          logo: response.data.data.logo || response.data.data
        }));
        
        // Reset file state after successful upload
        setLogoFile(null);
        
        // Show success message
        showSuccessToast('Logo uploaded successfully!');
      } else {
        showErrorToast('Failed to upload logo');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      showErrorToast('Error uploading logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const getStatusBadge = (isActive) => {
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
        isActive 
          ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' 
          : 'bg-red-100 text-red-800 border border-red-200'
      }`}>
        <div className={`w-2 h-2 rounded-full mr-2 ${isActive ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
        {isActive ? t('active') : t('inactive')}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">{t('loading_dashboard')}</p>
        </div>
      </div>
    );
  }

  if (!loading && !caterer) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="text-red-500 mb-6">
            <svg className="w-20 h-20 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Caterer Profile</h3>
          <p className="text-gray-600 mb-6">{error || 'Caterer not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="py-6">
        <div className="px-4 sm:px-6 lg:px-8">
          {/* Header Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="h-28 bg-gradient-to-r from-slate-100 to-white" />
            <div className="px-6 pb-6 -mt-12">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div className="flex items-end gap-4">
                  {/* Logo/Avatar Section */}
                  <div className="relative">
                    <div className="w-24 h-24 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-800 font-semibold text-2xl overflow-hidden">
                      {logoPreview ? (
                        <img 
                          src={logoPreview} 
                          alt={`${caterer.name} logo`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        caterer.name.charAt(0).toUpperCase()
                      )}
                    </div>
                    
                    {/* Logo Upload Button */}
                    <button
                      onClick={() => document.getElementById('logo-upload').click()}
                      className="absolute -bottom-2 -right-2 w-8 h-8 bg-blue-600 hover:bg-blue-700 rounded-full flex items-center justify-center text-white shadow-lg transition-colors"
                      title="Upload Logo"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </button>
                    
                    {/* Hidden file input */}
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="hidden"
                    />
                  </div>

                  {/* Logo Upload Actions */}
                  {logoFile && (
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={handleLogoUpload}
                        disabled={uploadingLogo}
                        className={`px-3 py-1.5 rounded-lg text-white text-xs font-medium transition-colors flex items-center gap-2 ${
                          uploadingLogo 
                            ? 'bg-gray-400 cursor-not-allowed' 
                            : 'bg-green-600 hover:bg-green-700'
                        }`}
                      >
                        {uploadingLogo && (
                          <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent"></div>
                        )}
                        {uploadingLogo ? 'Uploading...' : 'Save Logo'}
                      </button>
                      <button
                        onClick={() => {
                          setLogoFile(null);
                          setLogoPreview(caterer.logo || null);
                        }}
                        disabled={uploadingLogo}
                        className={`px-3 py-1.5 rounded-lg text-white text-xs font-medium transition-colors ${
                          uploadingLogo 
                            ? 'bg-gray-400 cursor-not-allowed' 
                            : 'bg-gray-600 hover:bg-gray-700'
                        }`}
                      >
                        Cancel
                      </button>
                    </div>
                  )}

                  <div>
                    <h1 className="text-2xl md:text-3xl font-semibold text-slate-900">{caterer.name}</h1>
                    <p className="text-slate-500 text-sm md:text-base">{caterer.city}, {caterer.country}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {getStatusBadge(caterer.isActive)}
                  <button
                    onClick={() => { setEditingCaterer(caterer); setIsModalOpen(true); }}
                    className="px-4 py-2 rounded-lg bg-gradient-to-r from-primary to-secondary text-white text-sm hover:opacity-90 shadow-sm"
                  >
                    {t('edit')}
                  </button>
                  <button
                    onClick={() => navigate('/caterers')}
                    className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 text-sm hover:bg-slate-50"
                  >
                    Back to Caterers
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Details Grid */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Contact Information */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-sm font-semibold text-slate-900 mb-4">{t('contact_information')}</h2>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Email</span>
                  <span className="text-slate-900">{caterer.email || t('not_provided')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('phone')}</span>
                  <span className="text-slate-900">{caterer.phone || t('not_provided')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('address')}</span>
                  <span className="text-slate-900">{caterer.address || t('not_provided')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('city')}</span>
                  <span className="text-slate-900">{caterer.city || t('not_provided')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('country')}</span>
                  <span className="text-slate-900">{caterer.country || t('not_provided')}</span>
                </div>
              </div>
            </div>

            {/* Business Details */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-sm font-semibold text-slate-900 mb-4">{t('business_details')}</h2>
              <div className="space-y-3 text-sm">
                {caterer.businessRegistrationNumber && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">{t('business_registration_number')}</span>
                    <span className="text-slate-900 font-mono">{caterer.businessRegistrationNumber}</span>
                  </div>
                )}
                {caterer.website && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">{t('website')}</span>
                    <a 
                      href={caterer.website.startsWith('http') ? caterer.website : `https://${caterer.website}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      {caterer.website}
                    </a>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('status')}</span>
                  <span className="text-slate-900">{caterer.isActive ? t('active') : t('inactive')}</span>
                </div>
              </div>
            </div>

            {/* Account Details */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-sm font-semibold text-slate-900 mb-4">{t('account_details')}</h2>
              <div className="space-y-3 text-sm">
                {/* <div className="flex justify-between">
                  <span className="text-slate-500">{t('account_id')}</span>
                  <span className="text-slate-900 font-mono break-all">{caterer._id}</span>
                </div> */}
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('created_date')}</span>
                  <span className="text-slate-900">
                    {caterer.createdAt ? new Date(caterer.createdAt).toLocaleDateString() : t('not_available')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('last_updated')}</span>
                  <span className="text-slate-900">
                    {caterer.updatedAt ? new Date(caterer.updatedAt).toLocaleDateString() : t('not_available')}
                  </span>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-sm font-semibold text-slate-900 mb-4">{t('additional_information')}</h2>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Owner Email</span>
                  <span className="text-slate-900">
                    {caterer.ownerId?.email || t('not_provided')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Owner Phone</span>
                  <span className="text-slate-900">
                    {caterer.ownerId?.phone || t('not_provided')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Owner Name</span>
                  <span className="text-slate-900">
                    {caterer.ownerId?.firstName && caterer.ownerId?.lastName 
                      ? `${caterer.ownerId.firstName} ${caterer.ownerId.lastName}`.trim()
                      : caterer.ownerId?.name || t('not_provided')
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Subscription Details */}
          {subscription && (
            <div className="mt-6">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-sm font-semibold text-slate-900 mb-4">Subscription Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Plan Information */}
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Plan Name</span>
                      <span className="text-slate-900 font-medium">{subscription.planName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Price</span>
                      <span className="text-slate-900 font-medium">₹{subscription.price}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Status</span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        subscription.status === 'active' 
                          ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' 
                          : 'bg-red-100 text-red-800 border border-red-200'
                      }`}>
                        <div className={`w-2 h-2 rounded-full mr-2 ${subscription.status === 'active' ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
                        {subscription.status}
                      </span>
                    </div>
                  </div>

                  {/* Date Information */}
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Start Date</span>
                      <span className="text-slate-900">
                        {subscription.startDate ? new Date(subscription.startDate).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">End Date</span>
                      <span className="text-slate-900">
                        {subscription.endDate ? new Date(subscription.endDate).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                    {/* <div className="flex justify-between">
                      <span className="text-slate-500">Payment ID</span>
                      <span className="text-slate-900 font-mono text-xs break-all">{subscription.paymentId}</span>
                    </div> */}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <AddEditCatererModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        caterer={editingCaterer}
        onSuccess={handleModalSuccess}
      />
    </>
  );
};

export default CatererProfile; 